import React, { useState } from 'react';
import myTdmLogo from './myTdmLogo3.png';
import DashboardLaunchpad from './components/DashboardLaunchpad';
import VancoCalculator from './components/VancoCalculator';
import AminoglycosideCalculator from './components/AminoglycosideCalculator';
import ValproicCalculator from './components/ValproicCalculator';
import PhenytoinCalculator from './components/PhenytoinCalculator';
import LithiumCalculator from './components/LithiumCalculator';
import CyclosporinCalculator from './components/CyclosporinCalculator';
import CarbamazepineCalculator from './components/CarbamazepineCalculator';
import PhenobarbitoneCalculator from './components/PhenobarbitoneCalculator';
import TheophyllineCalculator from './components/TheophyllineCalculator';
import TacrolimusCalculator from './components/TacrolimusCalculator';
import DigoxinCalculator from './components/DigoxinCalculator';
import SalicylateToxicityTracker from './components/SalicylateToxicityTracker';
import SirolimusCalculator from './components/SirolimusCalculator';
import MethotrexateCalculator from './components/MethotrexateCalculator';
import ParacetamolCalculator from './components/ParacetamolCalculator';
import RegisterPage from './components/RegisterPage'; // Direct gate mount
import { logCalculatorClick } from './components/supabaseClient';

function App() {
  // Navigation states: 'dashboard' or specific drug IDs
  const [activeTab, setActiveTab] = useState('dashboard');
  // showLanding controls whether we display the beautiful React-Native style welcome layout
  const [showLanding, setShowLanding] = useState(true);
  const [tourStep, setTourStep] = useState(0); // 0 means tour is inactive; 1-5 manages active tooltip viewports

  // 🔑 PERSISTENT AUTHENTICATION STATE (LIFTED TO TOP LAYER)
  const [globalUser, setGlobalUser] = useState(null);

  // Step 1: Feedback System States
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState('Feature Demand');
  const [userComment, setUserComment] = useState('');
  // 📊 NEW: Access Tiers Table State
  const [showTiersModal, setShowTiersModal] = useState(false);

  // Secret Developer Analytics Panel States
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [devPassword, setDevPassword] = useState('');
  const [isAuthed, setIsAuthed] = useState(false);

  // Aminoglycoside state sub-variables
  const [aminoDrug, setAminoDrug] = useState('gentamicin');
  const [aminoStrategy, setAminoStrategy] = useState('extended');

  // Helper dictionary to cleanly print plain titles on headers
  const drugNamesMap = {
    vanco: 'Vancomycin', amino: 'Aminoglycosides', valproic: 'Valproic Acid',
    pheny: 'Phenytoin', lithium: 'Lithium', cyclosporin: 'Cyclosporin',
    carbamazepine: 'Carbamazepine', phenobarbitone: 'Phenobarbitone',
    theophylline: 'Theophylline', tacrolimus: 'Tacrolimus', digoxin: 'Digoxin',
    salicylate: 'Salicylate', methotrexate: 'Methotrexate', paracetamol :'Paracetamol',
  };

  // Intercept and secure dropdown selections
  const handleDropdownChange = (drugId) => {
    if (drugId === 'vanco' || drugId === 'amino') {
      logCalculatorClick(drugId, globalUser ? globalUser.email : null);
      setActiveTab(drugId);
      setShowLanding(false);
      return;
    }

    if (globalUser) {
      logCalculatorClick(drugId, globalUser.email); // 👈 Passes the true logged-in pharmacist email dropdown click!
      setActiveTab(drugId);
      setShowLanding(false);
    } else {
      alert(`🔐 Access Restricted: Please sign in to open the premium ${drugNamesMap[drugId]} module.`);
      setActiveTab('login-gate');
      setShowLanding(false);
    }
  };

  const handleSaveFeedback = (e) => {
      e.preventDefault();
      if (!userComment.trim()) return alert("Please type your suggestion before submitting.");

      const existingLogs = JSON.parse(localStorage.getItem('mytdm_feedback_db')) || [];

      const newEntry = {
          id: Date.now(),
          date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
          category: feedbackCategory,
          comment: userComment.trim()
      };

      existingLogs.push(newEntry);
      localStorage.setItem('mytdm_feedback_db', JSON.stringify(existingLogs));

      setUserComment('');
      setShowFeedbackModal(false);
      alert("🚀 Demand recorded! Thank you for helping optimize the myTDM framework.");
  };

  React.useEffect(() => {
    // Check if the current URL contains a Supabase authentication recovery fragment
    const hash = window.location.hash;
    
    if (hash && (hash.includes('access_token=') && hash.includes('type=recovery'))) {
      // Alert the user so they know they are securely inside the system loop
      alert("🔑 Secure Password Recovery Mode verified! Please configure your new password now.");
      
      // Bypass landing view elements and pop open the view portal gate
      setShowLanding(false);
      setActiveTab('update-password');
      
      // Clean out the hash string from the URL bar so the token doesn't linger
      window.location.hash = '';
    }
  }, []);

  return (
    <div style={{ background: '#111416', minHeight: '100vh', color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      {/* 1. TOP GLOBAL NAVIGATION BANNER (React-Native Layout Style) */}
      <nav className="no-print" style={{
        background: '#1c2124', boxSizing: 'border-box', padding: '0 20px', height: '65px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid #2c3539', sticky: 'top', top: 0, zIndex: 1000, width: '100%', maxWidth: '100vw', overflowX: 'auto', 
        overflowY: 'hidden', whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch'
      }}>
        {/* Left Side: Brand Identity Logo */}
        <div 
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
          onClick={() => { setShowLanding(true); setActiveTab('dashboard'); }}
        >
          <img src={myTdmLogo}
            alt="myTDM Icon" 
            style={{ width: 'auto', height: '28px', borderRadius: '3px', objectFit: 'contain' }}
          />
          <span style={{ fontSize: '1.1rem', fontWeight: '700', letterSpacing: '0.5px' }}>
            my<span style={{ color: '#00d2ff' }}>TDM</span> Calculator
          </span>
        </div>

        {/* Right Side: Navigation Menu Button Items Grid */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '25px', fontSize: '0.9rem', fontWeight: '500', flexShrink: 0 }}>
          
          {/* DRUG NAV DROPDOWN SELECTOR */}
          <div style={{ position: 'relative', display: 'inline-block' }} className="dropdown-trigger">
            <select 
              value={activeTab === 'dashboard' || activeTab === 'login-gate' ? '' : activeTab}
              onChange={(e) => handleDropdownChange(e.target.value)}
              style={{ background: 'transparent', border: 'none', color: '#ecf0f1', fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer', outline: 'none' }}
            >

              <option value="" disabled style={{background: '#1c2124'}}>Calculators </option>
              <option value="vanco" style={{background: '#1c2124'}}>Vancomycin</option>
              <option value="amino" style={{background: '#1c2124'}}>Aminoglycosides</option>
              <option value="valproic" style={{background: '#1c2124'}}>Valproic Acid</option>
              <option value="pheny" style={{background: '#1c2124'}}>Phenytoin</option>
              <option value="carbamazepine" style={{background: '#1c2124'}}>Carbamazepine</option>
              <option value="phenobarbitone" style={{background: '#1c2124'}}>Phenobarbitone</option>
              <option value="lithium" style={{background: '#1c2124'}}>Lithium</option>
              <option value="cyclosporin" style={{background: '#1c2124'}}>Cyclosporin</option>
              <option value="theophylline" style={{background: '#1c2124'}}>Theophylline</option>
              <option value="tacrolimus" style={{background: '#1c2124'}}>Tacrolimus</option>
              <option value="digoxin" style={{background: '#1c2124'}}>Digoxin</option>
              <option value="salicylate" style={{background: '#1c2124'}}>Salicylate</option>
              <option value="sirolimus" style={{background: '#1c2124'}}>Sirolimus</option>
              <option value="methotrexate" style={{background: '#1c2124'}}>Methotrexate</option>
              <option value="paracetamol" style={{background: '#1c2124'}}>Paracetamol</option>
            </select>
          </div>

          <span style={navLinkStyle} onClick={() => setShowTiersModal(true)}>Pricing</span>
          <span style={navLinkStyle} onClick={() => setShowFeedbackModal(true)}>Feedback</span>
          
          {/* 👤 DYNAMIC AUTHENTICATION ACTION BUTTON */}
          {globalUser ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* 🏷️ PREMIUM ACCOUNT STATUS BADGE INDICATOR */}
              {globalUser.is_subscribed ? (
                <span style={{ padding: '4px 10px', background: '#2ecc71', color: '#fff', fontSize: '0.68rem', fontWeight: '800', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', boxShadow: '0 2px 4px rgba(46, 204, 113, 0.2)' }}>
                  👑 PRO
                </span>
              ) : (
                <span style={{ padding: '4px 10px', background: '#34495e', color: '#bdc3c7', fontSize: '0.68rem', fontWeight: '800', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  FREE
                </span>
              )}
              {/* SIGN OUT ELEMENT BUTTON */}
              <button 
                onClick={() => { setGlobalUser(null); setActiveTab('dashboard'); }}
                style={{ padding: '7px 14px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}
              >
                🚪 Sign Out ({globalUser.email.split('@')[0]})
              </button>
            </div>
          ) : (
            <button 
              onClick={() => { setShowLanding(false); setActiveTab('login-gate'); }}
              style={{ padding: '7px 14px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}
            >
              👤 Sign In
            </button>
          )}

        </div>
      </nav>

      {/* 2. MAIN CONTEXT CONTENT CONTAINER PORT */}
      {showLanding ? (
        /* HERO LANDING SPLASH VIEW PORT */
        <div className="no-print" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: 'calc(100vh - 65px)', padding: '40px 15px', background: 'radial-gradient(circle at center, #1b262c 0%, #111416 100%)',
          textAlign: 'center', width: '100%', maxWidth: '100vw', overflowX: 'hidden'
        }}>
          <style>{`
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}</style>
          <div style={{ maxWidth: '500px', marginTop: '35px', filter: 'drop-shadow(0 10px 25px rgba(0, 210, 255, 0.15))' }}>
            <img 
              src={myTdmLogo} 
              alt="myTDM Calculator - Therapeutic Drug Monitoring Solution" 
              style={{ width: '100%', height: 'auto', borderRadius: '12px',animation: 'spin infinite 20s linear' }}
              
            />
          </div>
          
          <h1 style={{ fontSize: 'clamp(2rem, 8vw, 3.2rem)', fontWeight: '800', width: '100%', maxWidth: '650px', margin: '10px 0',letterSpacing: '-1px', textAlign: 'center', padding: '0 15px'}}>
            Clinical TDM Assistant
          </h1>
          <p style={{ fontSize: '1.4rem', color: '#95a5a6', maxWidth: '650px', margin: '0 0 35px 0', fontWeight: '400', lineHeight: '1.4' }}>
            Calculate once, treat anywhere. Precision multi-drug clinical pharmacokinetics decision engine.
          </p>
          
          <div style={{ display: 'flex', gap: '20px' }}>
            <button 
              onClick={() => setShowLanding(false)} 
              style={{
                padding: '14px 30px', background: '#00d2ff', color: '#111416', border: 'none',
                borderRadius: '30px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(0,210,255,0.4)', transition: '0.2s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              Get Started
            </button>
            <button 
              onClick={() => { setShowLanding(false); setActiveTab('amino'); setTourStep(1) }} 
              style={{
                padding: '14px 30px', background: 'transparent', color: '#fff', border: '2px solid #3d4e5d',
                borderRadius: '30px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s'
              }}
            >
              Learn the Basics
            </button>
          </div>

          <div style={{ maxWidth: '950px', width: '100%',marginTop: '80px', borderTop: '1px solid #2c3539',boxSizing: 'border-box', paddingTop: '0 15px', textAlign: 'left' }}>
            <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#fff', marginBottom: '10px', fontWeight: '700' }}>
              What is my<span style={{color: '#00d2ff'}}>TDM</span> Calculator?
            </h2>
            <p style={{ textAlign: 'center', color: '#95a5a6', fontSize: '1.05rem', maxWidth: '700px', margin: '0 auto 40px auto', lineHeight: '1.6' }}>
              An interactive clinical TDM dosing calculator matrix to help ward pharmacists streamline Therapeutic Drug Monitoring decisions, reduce dosing errors, and optimize target concentrations.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '25px', marginBottom: '5px', width: '100%' }}>
              <div style={featureCardStyle}>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📊</div>
                <h3 style={{ color: '#00d2ff', margin: '0 0 10px 0', fontSize: '1.2rem' }}>Population Predictive Models</h3>
                <p style={{ color: '#bdc3c7', fontSize: '0.88rem', margin: 0, lineHeight: '1.5' }}>
                  Input patient-specific parameters like weight, age, and serum creatinine to compute expected pharmacokinetics instantly based on local handbook guidelines.
                </p>
              </div>

              <div style={featureCardStyle}>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🛡️</div>
                <h3 style={{ color: '#2ecc71', margin: '0 0 10px 0', fontSize: '1.2rem' }}>Non-Compliance Insulation</h3>
                <p style={{ color: '#bdc3c7', fontSize: '0.88rem', margin: 0, lineHeight: '1.5' }}>
                  The dose simulator is safely locked to population baselines. This prevents toxic over-titrations if low lab troughs were caused by missed doses rather than high clearance.
                </p>
              </div>

              <div style={featureCardStyle}>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏱️</div>
                <h3 style={{ color: '#f1c40f', margin: '0 0 10px 0', fontSize: '1.2rem' }}>Integrated Handbooks</h3>
                <p style={{ color: '#bdc3c7', fontSize: '0.88rem', margin: 0, lineHeight: '1.5' }}>
                  No more hunting through paper binders and manual calculation. Protocol timelines, target ranges, specific laboratory tube guidelines, and toxicity alert thresholds are embedded inside each workspace.
                </p>
              </div>
            </div>

            <div style={{ marginTop: '40px', padding: '25px', background: '#1c2226', borderRadius: '12px', border: '1px solid #2c3539' }}>
              <h4 style={{ color: '#fff', margin: '0 0 10px 0', fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Standard Clinical Workspace Workflow
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', textAlign: 'center', fontSize: '0.85rem', color: '#bdc3c7', marginTop: '15px' }}>
                <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                  <strong style={{ color: '#fff', display: 'block', marginBottom: '4px' }}>1. Profile</strong> Input patient size, renal metrics & age.
                </div>
                <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                  <strong style={{ color: '#00d2ff', display: 'block', marginBottom: '4px' }}>2. Stratify</strong> Select exact disease indication & timelines.
                </div>
                <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                  <strong style={{ color: '#e67e22', display: 'block', marginBottom: '4px' }}>3. Correlate</strong> Input lab values & crosscheck variance.
                </div>
                <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                  <strong style={{ color: '#2ecc71', display: 'block', marginBottom: '4px' }}>4. Simulate</strong> Safely titrate & generate clinical actions.
                </div>
              </div>
            </div>
          </div>
        </div>
        
      ) : (
        /* WORKSPACE WORK AREA ENGINE */
        <div style={{ padding: '40px 20px', maxWidth: '1000px', margin: '0 auto' }}>
          
          {/* Dynamic Breadcrumb Header Track */}
          <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', paddingBottom: '12px', borderBottom: '1px solid #232b30' }}>
            <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
              Current Workspace Scope: <span style={{ color: '#00d2ff', fontWeight: 'bold' }}>
                {activeTab === 'dashboard' ? 'Home Launchpad Console' : drugNamesMap[activeTab]}
              </span>
            </div>
            {activeTab !== 'dashboard' && activeTab !== 'login-gate' && (
              <button 
                onClick={() => setActiveTab('dashboard')}
                style={{ padding: '6px 12px', background: '#232b30', color: '#95a5a6', border: '1px solid #343f46', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
              >
                🎛️ Return to Grid Menu
              </button>
            )}
          </div>

          {/* RENDERING HOOK CONSOLE ROUTING WITH SHARED AUTH STATE HOOKS */}
          {activeTab === 'dashboard' && (
            <DashboardLaunchpad 
              user={globalUser} 
              setUser={setGlobalUser} 
              onSelectDrug={(drugId) => setActiveTab(drugId)} 
            />
          )}

          {/* ✅ FIXED: ALL INDIVIDUAL DRUG SUITES NOW HAVE ACCESS TO THE TOP LAYER SESSION PROPS */}
          {activeTab === 'vanco' && <VancoCalculator user={globalUser} />}
          
          {activeTab === 'amino' && (
            <div style={{ position: 'relative' }}>
                {tourStep > 0 && tourStep < 5 && (
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 998, pointerEvents: 'none' }} />
                )}
                <div>
                  <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', padding: '20px', background: '#1c2226', borderRadius: '12px', border: '1px solid #2c3539', marginBottom: '25px', position: 'relative' }}>
                    
                    {/* COLUMN 1: TARGET MEDICINE (TOUR STEP 1) */}
                    <div style={{ position: 'relative', zIndex: tourStep === 1 ? 999 : 1 }}>
                      <label style={{ fontSize: '0.75rem', color: '#95a5a6', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>TARGET METRIC REGIMEN:</label>
                      <select value={aminoDrug} onChange={(e) => setAminoDrug(e.target.value)} style={selectInputStyle}>
                        <option value="gentamicin">Gentamicin (Base Module)</option>
                        <option value="amikacin">Amikacin (High-Dose Module)</option>
                      </select>

                      {tourStep === 1 && (
                        <div style={tourTooltipStyle('bottom')}>
                          <h5 style={tourTitleStyle}>🎓 Step 1: Drug Selection</h5>
                          <p style={{ margin: '0 0 10px 0' }}>Select between **Gentamicin** or **Amikacin**. The population baseline adapts instantly.</p>
                          <button onClick={() => setTourStep(2)} style={tourBtnStyle}>Next ➡️</button>
                        </div>
                      )}
                    </div>

                    {/* COLUMN 2: STRATEGY SWITCHER (TOUR STEP 2) */}
                    <div style={{ position: 'relative', zIndex: tourStep === 2 ? 999 : 1 }}>
                      <label style={{ fontSize: '0.75rem', color: '#95a5a6', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>KINETIC TREATMENT STRATEGY:</label>
                      <select value={aminoStrategy} onChange={(e) => setAminoStrategy(e.target.value)} style={selectInputStyle}>
                        <option value="extended">Extended Interval (Hartford Nomogram)</option>
                        <option value="traditional">Traditional (Multiple Daily Peak/Trough)</option>
                      </select>

                      {tourStep === 2 && (
                        <div style={tourTooltipStyle('bottom')}>
                          <h5 style={tourTitleStyle}>⚙️ Step 2: Strategic Modality</h5>
                          <p style={{ margin: '0 0 10px 0' }}>Toggle between high-dose **Extended Interval** guidelines or traditional **Multiple Daily Dosing** math tracks.</p>
                          <button onClick={() => setTourStep(3)} style={tourBtnStyle}>Next ➡️</button>
                        </div>
                      )}
                    </div>

                  </div>

                  <div style={{ position: 'relative', zIndex: (tourStep === 3 || tourStep === 4) ? 999 : 1 }}>
                    {tourStep === 3 && (
                      <div style={{ ...tourTooltipStyle('top'), bottom: '97%', left: '100px', width: '350px' }}>
                        <h5 style={tourTitleStyle}>📋 Step 3: Reference Handbooks</h5>
                        <p style={{ margin: '0 0 10px 0' }}>Expand this accordion bar to read target ranges and specific drawing timelines.</p>
                        <button onClick={() => setTourStep(4)} style={tourBtnStyle}>Next ➡️</button>
                      </div>
                    )}

                    <AminoglycosideCalculator user={globalUser} aminoDrug={aminoDrug} aminoStrategy={aminoStrategy} />

                    {tourStep === 4 && (
                      <div style={{ ...tourTooltipStyle('top'), bottom: 'auto', top: '240px', left: '50%', transform: 'translateX(-50%)', width: '380px' }}>
                        <h5 style={tourTitleStyle}>👤 Step 4: Patient Metrics</h5>
                        <p style={{ margin: '0 0 10px 0' }}>Input the patient profile details (Gender, Weight, Creatinine) to compute target baseline clearances.</p>
                        <button onClick={() => setTourStep(5)} style={tourBtnStyle}>Next ➡️</button>
                      </div>
                    )}
                  </div>
                </div>

                {tourStep === 5 && (
                  <div style={{
                    position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    background: '#1c2226', border: '3px solid #f1c40f', borderRadius: '12px',
                    padding: '30px', width: '480px', zIndex: 10000, boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>⚖️</div>
                    <h4 style={{ color: '#f1c40f', margin: '0 0 12px 0', fontSize: '1.3rem' }}>Final Step: Clinical Self-Interpretation</h4>
                    <p style={{ color: '#ecf0f1', fontSize: '0.88rem', lineHeight: '1.5', margin: '0 0 20px 0' }}>
                      The math templates provide guidance based on handbook populations. Clinicians must apply independent judgment to verify samples against current compliance and clinical status.
                    </p>
                    <button onClick={() => setTourStep(0)} style={{ padding: '8px 20px', background: '#2ecc71', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                      🎉 Complete Guide Tour
                    </button>
                  </div>
                )}
            </div>
          )}

          {/* AUTH GATE PAGE CONTAINER */}
          {activeTab === 'login-gate' && (
            <RegisterPage 
              onAuthSuccess={(user) => { setGlobalUser(user); setActiveTab('dashboard'); }}
              onBackToDashboard={() => setActiveTab('dashboard')}
              viewMode="login" // Pass explicit view modes if handled
            />
          )}
          {/* 🔑 NEW RECOVERY FORM TRACK CONFIGURATION GATE */}
            {activeTab === 'update-password' && (
              <RegisterPage 
                onAuthSuccess={() => { setActiveTab('login-gate'); }} 
                onBackToDashboard={() => setActiveTab('dashboard')}
                viewMode="recovery" 
              />
            )}

          {activeTab === 'valproic' && <ValproicCalculator user={globalUser} />}
          {activeTab === 'pheny' && <PhenytoinCalculator user={globalUser} />}
          {activeTab === 'carbamazepine' && <CarbamazepineCalculator user={globalUser} />}
          {activeTab === 'phenobarbitone' && <PhenobarbitoneCalculator user={globalUser} />}
          {activeTab === 'lithium' && <LithiumCalculator user={globalUser} />}
          {activeTab === 'cyclosporin' && <CyclosporinCalculator user={globalUser} />}
          {activeTab === 'theophylline' && <TheophyllineCalculator user={globalUser} />}
          {activeTab === 'tacrolimus' && <TacrolimusCalculator user={globalUser} />}
          {activeTab === 'digoxin' && <DigoxinCalculator user={globalUser} />}
          {activeTab === 'salicylate' && <SalicylateToxicityTracker user={globalUser} />}
          {activeTab === 'sirolimus' && <SirolimusCalculator user={globalUser} />}
          {activeTab === 'methotrexate' && <MethotrexateCalculator user={globalUser} />}
          {activeTab === 'paracetamol' && <ParacetamolCalculator user={globalUser} />}
        </div>
      )}
      
      {/* 3. SAFETY LEGAL CLINICAL DISCLAIMER FOOTER */}
      <footer style={{ padding: '30px 20px', borderTop: '1px solid #232b30', backgroundColor: '#0b0d0e', textAlign: 'center', fontSize: '0.75rem', color: '#68757e', lineHeight: '1.6' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <p><strong>CLINICAL DISCLAIMER:</strong> This pharmacokinetic decision-support software is optimized exclusively for authorized medical specialists. All math routines are compiled from Clinical Pharmacokinetic Pharmacy Handbook Second Edition, MOH.</p>
          <p 
            style={{ marginTop: '6px', color: '#444e55', cursor: 'pointer' }} 
            onClick={() => { setShowStatsModal(true); setIsAuthed(false); setDevPassword(''); }}
          >
            Platform Build: Production Engine Core | v1.5.2-Beta (Open Access Edition)
          </p>
        </div>
      </footer>

      {/* 4. GLOBAL PRINT EMULATION LAYER OVERRIDES */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media print {
          .no-print { display: none !important; }
          body, html { background: #fff !important; color: #000 !important; padding: 0 !important; margin: 0 !important; }
          div, section, p, span, h1, h2, h3, h4, strong { background: transparent !important; color: #000 !important; border-color: #ccc !important; box-shadow: none !important; }
          input, select { border: none !important; background: transparent !important; color: #000 !important; appearance: none; -webkit-appearance: none; }
        }
      `}</style>

      {/* 📊 INTERACTIVE ACCESS TIERS MATRIX MODAL LAYOUT */}
      {showTiersModal && (
        <div style={modalBackdropStyle}>
          <div style={modalContainerStyle('3px solid #00d2ff', '750px')}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#00d2ff', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem', fontWeight: 'bold' }}>
                🛡️ myTDM Platform Access Tiers
              </h3>
              <button onClick={() => setShowTiersModal(false)} style={modalCancelBtnStyle}>Close Matrix</button>
            </div>

            {/* Matrix Data Grid Table Wrapper */}
            <div style={{ background: '#0e1316', borderRadius: '8px', padding: '15px', border: '1px solid #2c3539' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #2c3539', color: '#00d2ff' }}>
                    <th style={{ padding: '12px 8px', width: '25%' }}>Features & Tools</th>
                    <th style={{ padding: '12px 8px', width: '25%', textAlign: 'center' }}>🟢 Free</th>
                    <th style={{ padding: '12px 8px', width: '25%', textAlign: 'center' }}>⚡ Premium Clinical</th>
                    
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #1c2429' }}>
                    <td style={{ padding: '12px 8px', fontWeight: 'bold', color: '#95a5a6' }}>Subscribe Plan</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#e74c3c', fontSize: '1.1rem' }}>$0 / mo</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#2ecc71', fontSize: '1.1rem' }}>$0 / mo</td>
                    
                  </tr>
                  <tr style={{ borderBottom: '1px solid #1c2429' }}>
                    <td style={{ padding: '12px 8px', fontWeight: 'bold', color: '#95a5a6' }}>Login</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#e74c3c', fontSize: '1.1rem' }}>❌ Not Required</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#2ecc71', fontSize: '1.1rem' }}>✔️ Required</td>
                    
                  </tr>
                  <tr style={{ borderBottom: '1px solid #1c2429' }}>
                    <td style={{ padding: '12px 8px', fontWeight: 'bold', color: '#95a5a6' }}>Base Math Tracks</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#fff', fontSize: '0.8rem' }}>
                      Vancomycin<br/>Aminoglycosides
                    </td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#2ecc71', fontSize: '1.1rem' }}>✔️ Included</td>
                    
                  </tr>

                  <tr style={{ borderBottom: '1px solid #1c2429' }}>
                    <td style={{ padding: '12px 8px', fontWeight: 'bold', color: '#95a5a6' }}>Extended Modules</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#e74c3c', fontSize: '1.1rem' }}>❌ Locked</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#fff', fontSize: '0.8rem' }}>
                      All 15 Calculators<br/>(Lithium, Pheny, etc.)
                    </td>
                    
                  </tr>

                  <tr style={{ borderBottom: '1px solid #1c2429' }}>
                    <td style={{ padding: '12px 8px', fontWeight: 'bold', color: '#95a5a6' }}>Print PDF Report</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#e74c3c', fontSize: '1.1rem' }}>✔️ Included</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center', color: '#e74c3c', fontSize: '1.1rem' }}>✔️ Included</td>
                  
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowTiersModal(false)} 
                style={{ padding: '10px 24px', background: '#00d2ff', color: '#111416', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* IN-APP USER FEEDBACK MODAL */}
      {showFeedbackModal && (
        <div style={modalBackdropStyle}>
          <div style={modalContainerStyle('3px solid #00d2ff')}>
            <h3 style={{ margin: '0 0 5px 0', color: '#00d2ff', fontSize: '1.4rem' }}>💡 Clinical Wishlist & Feedback Loop</h3>
            <p style={{ color: '#95a5a6', fontSize: '0.85rem', margin: '0 0 20px 0' }}>Submit your feature demands or bug logs directly to the system development roadmap.</p>
            
            <form onSubmit={handleSaveFeedback}>
              <div style={{ marginBottom: '15px' }}>
                <label style={modalLabelStyle}>CLASSIFY YOUR DEMAND:</label>
                <select 
                  value={feedbackCategory} 
                  onChange={(e) => setFeedbackCategory(e.target.value)}
                  style={modalInputStyle}
                >
                  <option value="Feature Demand">🚀 Request New Drug Matrix / Feature Expansion</option>
                  <option value="Bug Report">⚠️ Report an Engine Calculation Bug</option>
                  <option value="UI Suggestion">🎨 Layout / Readability Suggestion</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={modalLabelStyle}>YOUR FEEDBACK / DEMAND DESCRIPTION:</label>
                <textarea 
                  rows="4"
                  value={userComment}
                  onChange={(e) => setUserComment(e.target.value)}
                  placeholder="What tool or modification can help your ward workflow? (e.g., Please add a Heparin Nomogram module next...)"
                  style={{ ...modalInputStyle, resize: 'none', fontFamily: 'inherit', lineHeight: '1.4' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" onClick={() => setShowFeedbackModal(false)} style={modalCancelBtnStyle}>Cancel</button>
                <button type="submit" style={modalSubmitBtnStyle}>Submit Demand</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SECRET DEVELOPER PORTAL */}
      {showStatsModal && (
        <div style={modalBackdropStyle}>
          <div style={modalContainerStyle(isAuthed ? '3px solid #2ecc71' : '3px solid #e74c3c', '700px')}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: isAuthed ? '#2ecc71' : '#e74c3c' }}>
                {isAuthed ? '📊 Core User Demand Analytics' : '🔐 Developer Authentication'}
              </h3>
              <button onClick={() => setShowStatsModal(false)} style={modalCancelBtnStyle}>Close</button>
            </div>

            {!isAuthed ? (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <p style={{ fontSize: '0.9rem', color: '#bdc3c7', marginBottom: '15px' }}>Enter your administrator security passkey to extract browser feedback telemetry statistics.</p>
                <input 
                  type="password" 
                  placeholder="Enter Dev Passkey" 
                  value={devPassword}
                  onChange={(e) => setDevPassword(e.target.value)}
                  style={{ ...modalInputStyle, width: '60%', textAlign: 'center', marginBottom: '15px' }}
                />
                <div>
                  <button 
                    onClick={() => {
                      if (devPassword === 'admin3213') {
                        setIsAuthed(true);
                      } else {
                        alert("Invalid security authentication key.");
                      }
                    }}
                    style={modalSubmitBtnStyle}
                  >
                    Unlock Metrics Database
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {(() => {
                  const logs = JSON.parse(localStorage.getItem('mytdm_feedback_db')) || [];
                  const features = logs.filter(l => l.category === 'Feature Demand').length;
                  const bugs = logs.filter(l => l.category === 'Bug Report').length;
                  const ui = logs.filter(l => l.category === 'UI Suggestion').length;

                  return (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '20px', textAlign: 'center' }}>
                        <div style={{ background: '#111416', padding: '10px', borderRadius: '6px' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#fff' }}>{logs.length}</div>
                          <div style={{ fontSize: '0.65rem', color: '#95a5a6' }}>TOTAL LOGS</div>
                        </div>
                        <div style={{ background: '#111416', padding: '10px', borderRadius: '6px' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#00d2ff' }}>{features}</div>
                          <div style={{ fontSize: '0.65rem', color: '#00d2ff' }}>DRUG DEMANDS</div>
                        </div>
                        <div style={{ background: '#111416', padding: '10px', borderRadius: '6px' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#e74c3c' }}>{bugs}</div>
                          <div style={{ fontSize: '0.65rem', color: '#e74c3c' }}>BUG LOGS</div>
                        </div>
                        <div style={{ background: '#111416', padding: '10px', borderRadius: '6px' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#f1c40f' }}>{ui}</div>
                          <div style={{ fontSize: '0.65rem', color: '#f1c40f' }}>UI SUGGESTIONS</div>
                        </div>
                      </div>

                      <div style={{ maxHeight: '250px', overflowY: 'auto', background: '#111416', borderRadius: '6px', padding: '10px' }}>
                        {logs.length === 0 ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', fontSize: '0.85rem' }}>No telemetry requests submitted from the wards yet.</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', textAlign: 'left' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #2c3539', color: '#95a5a6' }}>
                                <th style={{ padding: '6px' }}>Date</th>
                                <th style={{ padding: '6px' }}>Classification</th>
                                <th style={{ padding: '6px' }}>User Statement</th>
                              </tr>
                            </thead>
                            <tbody>
                              {logs.map(log => (
                                <tr key={log.id} style={{ borderBottom: '1px solid #2c3539' }}>
                                  <td style={{ padding: '6px', color: '#95a5a6', whiteSpace: 'nowrap' }}>{log.date}</td>
                                  <td style={{ padding: '6px', fontWeight: 'bold', color: log.category === 'Bug Report' ? '#e74c3c' : log.category === 'Feature Demand' ? '#00d2ff' : '#f1c40f' }}>{log.category}</td>
                                  <td style={{ padding: '6px', color: '#fff' }}>{log.comment}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                      <button 
                        onClick={() => { if(window.confirm("Purge complete local statistic logs?")) { localStorage.removeItem('mytdm_feedback_db'); setIsAuthed(false); setShowStatsModal(false); } }} 
                        style={{ marginTop: '15px', float: 'left', padding: '4px 10px', background: 'transparent', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}
                      >
                        🗑️ Purge Log Database
                      </button>
                      <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #2c3539', display: 'flex', gap: '10px' }}>
                        <button 
                          onClick={() => {
                            const logs = JSON.parse(localStorage.getItem('mytdm_feedback_db')) || [];
                            if (logs.length === 0) return alert("No data available to export.");
                            
                            let csvContent = "data:text/csv;charset=utf-8,Date,Classification,User Comment\n";
                            logs.forEach(log => {
                              const cleanComment = log.comment.replace(/"/g, '""');
                              csvContent += `"${log.date}","${log.category}","${cleanComment}"\n`;
                            });

                            const encodedUri = encodeURI(csvContent);
                            const link = document.createElement("a");
                            link.setAttribute("href", encodedUri);
                            link.setAttribute("download", `myTDM_Feedback_Export_${new Date().toISOString().split('T')[0]}.csv`);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          style={{ padding: '6px 12px', background: '#2ecc71', color: '#111416', border: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          📥 Download Excel Spreadsheet (.CSV)
                        </button>

                        <button 
                          onClick={() => {
                            const rawData = localStorage.getItem('mytdm_feedback_db') || "[]";
                            if (rawData === "[]") return alert("No data available to copy.");
                            
                            navigator.clipboard.writeText(rawData);
                            alert("📋 Raw data copied to clipboard! You can paste this directly into any text file or Cloud database console.");
                          }}
                          style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #00d2ff', color: '#00d2ff', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          🔗 Copy Raw Cloud JSON
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Minimalist layout configurations
const navLinkStyle = {
  color: '#b2bec3',
  cursor: 'pointer',
  transition: 'color 0.2s'
};

const selectInputStyle = {
  width: '100%',
  padding: '10px',
  background: '#2c3e50',
  border: '1px solid #34495e',
  color: '#fff',
  borderRadius: '6px',
  fontSize: '0.9rem',
  outline: 'none'
};

const featureCardStyle = {
  background: '#1c2124',
  border: '1px solid #2c3539',
  borderRadius: '10px',
  padding: '25px',
  boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
  display: 'flex',
  flexDirection: 'column'
};

const tourTooltipStyle = (position) => ({
  position: 'absolute',
  left: '0',
  right: '0',
  bottom: position === 'top' ? '110%' : 'auto',
  top: position === 'bottom' ? '110%' : 'auto',
  background: '#00d2ff',
  color: '#111416',
  padding: '15px',
  borderRadius: '8px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
  zIndex: 10000,
  fontSize: '0.85rem',
  lineHeight: '1.4',
  marginTop: position === 'bottom' ? '8px' : '0',
  marginBottom: position === 'top' ? '8px' : '0',
  animation: 'fadeInUp 0.2s ease-out'
});

const tourTitleStyle = {
  margin: '0 0 6px 0',
  fontSize: '0.95rem',
  fontWeight: 'bold',
  color: '#111416',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const tourBtnStyle = {
  padding: '5px 12px',
  background: '#111416',
  color: '#00d2ff',
  border: 'none',
  borderRadius: '4px',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '0.75rem',
  float: 'right'
};

const modalBackdropStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.75)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 5000,
  animation: 'fadeIn 0.2s ease-out'
};

const modalContainerStyle = (borderStyle, customWidth = '450px') => ({
  background: '#1c2124', border: borderStyle, borderRadius: '12px',
  padding: '25px', width: customWidth, maxWidth: '90%',
  boxShadow: '0 10px 30px rgba(0,0,0,0.5)', boxSizing: 'border-box'
});

const modalLabelStyle = {
  fontSize: '0.7rem', fontWeight: 'bold', color: '#95a5a6',
  display: 'block', marginBottom: '5px', letterSpacing: '0.5px'
};

const modalInputStyle = {
  width: '100%', padding: '10px', background: '#2c3e50',
  border: '1px solid #34495e', color: '#fff', borderRadius: '6px',
  fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box'
};

const modalCancelBtnStyle = {
  padding: '8px 16px', background: 'transparent', color: '#95a5a6',
  border: '1px solid #343f46', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem'
};

const modalSubmitBtnStyle = {
  padding: '8px 18px', background: '#00d2ff', color: '#111416',
  border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem'
};

export default App;